/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { getProjectKeyOrId } from '@jira/router-resources__utils/src/getProjectKeyOrId.tsx';
import { alphabeticalSort } from '@atlassian/jira-common-util-software-filters-sort/src/index.tsx';
import {
	ADMIN_PAGE_MODULE,
	BACKLOG_ACTION_MODULE,
	BOARD_ACTION_MODULE,
	PERSONAL_SETTINGS_PAGE_MODULE,
	PROJECT_PAGE_MODULE,
	PROJECT_SETTINGS_PAGE_MODULE,
	GLOBAL_PAGE_MODULE,
	CUSTOM_FIELD_TYPE_MODULE,
	SERVICEDESK_QUEUE_PAGE_MODULE,
	CUSTOMER_SERVICE_MANAGEMENT_QUEUE_PAGE_MODULE,
	SOURCE_NAVIGATION,
	SOURCE_FULL_PAGE_APP,
	SOURCE_ROUTER,
	SPRINT_ACTION_MODULE,
} from '@jira/forge__forge-ui-constants/src/constants.tsx';
import type {
	AdminPage,
	BacklogAction,
	BoardAction,
	GlobalPage,
	ProjectPage,
	ProjectSettingsPage,
	ServiceDeskQueuePage,
	FullPage,
	CustomFieldType,
	SprintAction,
	BoardViewActions,
	PersonalSettingsPage,
} from '@jira/forge__forge-ui-types/src/common/types/extension.tsx';
import type {
	FullPageModule,
	FullPageModuleWithAccessNarrowing,
} from '@jira/forge__forge-ui-types/src/common/types/module.tsx';
import type { DataClassificationProps } from '@jira/forge__forge-ui-utils/src/types.tsx';
import { fetchModules } from '@jira/forge__forge-ui-utils/src/utils/fetch-modules/doFetchWithGraphQLErrorsTracking.tsx';
import {
	// eslint-disable-next-line jira/restricted/@atlassian/react-resource-router
	createResource,
	useResource,
	type RouteResource,
	type Match,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import { uniqueExtensionsFilter, adminPageFilter } from '../../utils/forge-filters/index.tsx';

export const validateDataClassification = (
	params: Match,
	moduleName: FullPageModule,
): DataClassificationProps => {
	const isModuleWithAccessNarrowing = [
		SERVICEDESK_QUEUE_PAGE_MODULE,
		CUSTOMER_SERVICE_MANAGEMENT_QUEUE_PAGE_MODULE,
		PROJECT_PAGE_MODULE,
		PROJECT_SETTINGS_PAGE_MODULE,
	] // oxlint-disable-next-line typescript/consistent-type-assertions
		.includes(moduleName as FullPageModuleWithAccessNarrowing);

	const projectIdOrKey = getProjectKeyOrId(params);
	if (typeof projectIdOrKey === 'string' && projectIdOrKey !== '' && isModuleWithAccessNarrowing) {
		return { projectIdOrKey };
	}
	return null;
};

export const forgeAdminModuleResource: RouteResource<AdminPage[]> = createResource<AdminPage[]>({
	type: 'FORGE_ADMIN_MODULE',
	getKey: () => 'extensions',
	getData: (_, { tenantContext: { cloudId, isAnonymous } }) =>
		fetchModules({
			cloudId,
			isAnonymous,
			types: [ADMIN_PAGE_MODULE],
			context: {},
			includeHidden: true,
			source: SOURCE_FULL_PAGE_APP,
		}).then((modules) => normalizeModules(modules[ADMIN_PAGE_MODULE], adminPageFilter)),
	maxAge: Number.MAX_SAFE_INTEGER,
});

export const getNavigationSidebarGlobalSettingsResources = (): RouteResource<AdminPage[]>[] => [
	forgeAdminModuleResource,
];

export const forgeProjectModuleResource: RouteResource<ProjectPage[]> = createResource<
	ProjectPage[]
>({
	type: 'FORGE_PROJECT_MODULE',
	getKey: (routeContext) => {
		const projectKeyOrId = getProjectKeyOrId(routeContext.match);
		return `extensions-${projectKeyOrId}`;
	},
	getData: (routeContext, { tenantContext: { cloudId, isAnonymous } }) =>
		fetchModules({
			cloudId,
			isAnonymous,
			types: [PROJECT_PAGE_MODULE],
			context: { projectKey: getProjectKeyOrId(routeContext.match) },
			includeHidden: true,
			source: SOURCE_FULL_PAGE_APP,
		}).then((modules) => normalizeModules(modules[PROJECT_PAGE_MODULE], uniqueExtensionsFilter)),
	maxAge: Number.MAX_SAFE_INTEGER,
	isCritical: true,
});

export const forgeBacklogModuleResource: RouteResource<BacklogAction[]> = createResource<
	BacklogAction[]
>({
	type: 'FORGE_BACKLOG_MODULE',
	getKey: (routeContext) => {
		const projectKeyOrId = getProjectKeyOrId(routeContext.match);
		return `extensions-${projectKeyOrId}`;
	},
	getData: (routeContext, { tenantContext: { cloudId, isAnonymous } }) =>
		fetchModules({
			cloudId,
			isAnonymous,
			types: [BACKLOG_ACTION_MODULE],
			context: { projectKey: getProjectKeyOrId(routeContext.match) },
			includeHidden: false,
			source: SOURCE_ROUTER,
		}).then((modules) => normalizeModules(modules[BACKLOG_ACTION_MODULE], uniqueExtensionsFilter)),
	maxAge: Number.MAX_SAFE_INTEGER,
});

export const forgeBoardModuleResource: RouteResource<BoardAction[]> = createResource<BoardAction[]>(
	{
		type: 'FORGE_BOARD_MODULE',
		getKey: (routeContext) => {
			const projectKeyOrId = getProjectKeyOrId(routeContext.match);
			return `extensions-${projectKeyOrId}`;
		},
		getData: (routeContext, { tenantContext: { cloudId, isAnonymous } }) =>
			fetchModules({
				cloudId,
				isAnonymous,
				types: [BOARD_ACTION_MODULE],
				context: { projectKey: getProjectKeyOrId(routeContext.match) },
				includeHidden: false,
				source: SOURCE_ROUTER,
			}).then((modules) => normalizeModules(modules[BOARD_ACTION_MODULE], uniqueExtensionsFilter)),
		maxAge: Number.MAX_SAFE_INTEGER,
	},
);

export const forgeSprintModuleResource: RouteResource<SprintAction[]> = createResource<
	SprintAction[]
>({
	type: 'FORGE_SPRINT_MODULE',
	getKey: (routeContext) => {
		const projectKeyOrId = getProjectKeyOrId(routeContext.match);
		return `extensions-${projectKeyOrId}`;
	},
	getData: (routeContext, { tenantContext: { cloudId, isAnonymous } }) =>
		fetchModules({
			cloudId,
			isAnonymous,
			types: [SPRINT_ACTION_MODULE],
			context: { projectKey: getProjectKeyOrId(routeContext.match) },
			includeHidden: false,
			source: SOURCE_ROUTER,
		}).then((modules) => normalizeModules(modules[SPRINT_ACTION_MODULE], uniqueExtensionsFilter)),
	maxAge: Number.MAX_SAFE_INTEGER,
});

export const forgePersonalSettingsPageModuleResource: RouteResource<PersonalSettingsPage[]> =
	createResource<PersonalSettingsPage[]>({
		type: 'FORGE_PERSONAL_SETTINGS_MODULE',
		getKey: () => 'extensions',
		getData: (_, { tenantContext: { cloudId, isAnonymous } }) =>
			fetchModules({
				cloudId,
				isAnonymous,
				types: [PERSONAL_SETTINGS_PAGE_MODULE],
				context: {},
				includeHidden: false,
				source: SOURCE_FULL_PAGE_APP,
			}).then((modules) =>
				normalizeModules(modules[PERSONAL_SETTINGS_PAGE_MODULE], uniqueExtensionsFilter),
			),
		maxAge: Number.MAX_SAFE_INTEGER,
	});

export const getNavigationForgePersonalSettingsPageResources = (): RouteResource<
	PersonalSettingsPage[]
>[] => [forgePersonalSettingsPageModuleResource];

export const forgeProjectSettingsModuleResource: RouteResource<ProjectSettingsPage[]> =
	createResource<ProjectSettingsPage[]>({
		type: 'FORGE_PROJECT_SETTINGS_MODULE',
		getKey: (routeContext) => {
			const projectKeyOrId = getProjectKeyOrId(routeContext.match);
			return `extensions-${projectKeyOrId}`;
		},
		getData: (routeContext, { tenantContext: { cloudId, isAnonymous } }) =>
			fetchModules({
				cloudId,
				isAnonymous,
				types: [PROJECT_SETTINGS_PAGE_MODULE],
				context: { projectKey: getProjectKeyOrId(routeContext.match) },
				includeHidden: true,
				source: SOURCE_FULL_PAGE_APP,
			}).then((modules) =>
				normalizeModules(modules[PROJECT_SETTINGS_PAGE_MODULE], uniqueExtensionsFilter),
			),
		maxAge: Number.MAX_SAFE_INTEGER,
		isCritical: true,
	});

export const useForgeProjectSettingsModuleResource = (): UseResourceHookResponse<
	ProjectSettingsPage[]
> => useResource(forgeProjectSettingsModuleResource);

export const forgeGlobalModuleResource: RouteResource<GlobalPage[]> = createResource<GlobalPage[]>({
	type: 'FORGE_GLOBAL_MODULE',
	getKey: () => 'extensions',
	getData: (_, { tenantContext: { cloudId, isAnonymous } }) =>
		fetchModules({
			cloudId,
			isAnonymous,
			types: [GLOBAL_PAGE_MODULE],
			context: {},
			includeHidden: true,
			source: SOURCE_FULL_PAGE_APP,
		}).then((modules) => normalizeModules(modules[GLOBAL_PAGE_MODULE], uniqueExtensionsFilter)),
	maxAge: Number.MAX_SAFE_INTEGER,
});

export const getNavigationForgeGlobalPageResources = (): RouteResource<GlobalPage[]>[] => [
	forgeGlobalModuleResource,
];

export const forgeServiceDeskQueueModuleResource: RouteResource<ServiceDeskQueuePage[]> =
	createResource<ServiceDeskQueuePage[]>({
		type: 'FORGE_SERVICEDESK_QUEUE_MODULE',
		getKey: (routeContext) => {
			const projectKeyOrId = getProjectKeyOrId(routeContext.match);
			return `extensions-${projectKeyOrId}`;
		},
		getData: (routeContext, { tenantContext: { cloudId, isAnonymous } }) =>
			fetchModules({
				cloudId,
				isAnonymous,
				types: [SERVICEDESK_QUEUE_PAGE_MODULE],
				context: { projectKey: getProjectKeyOrId(routeContext.match) },
				includeHidden: true,
				source: SOURCE_FULL_PAGE_APP,
			}).then((modules) =>
				normalizeModules(modules[SERVICEDESK_QUEUE_PAGE_MODULE], uniqueExtensionsFilter),
			),
		maxAge: Number.MAX_SAFE_INTEGER,
	});

// oxlint-disable-next-line typescript/consistent-type-assertions
export const forgeCustomFieldTypeResource = createResource<CustomFieldType[]>({
	type: 'FORGE_CUSTOM_FIELD_TYPE_MODULE',
	getKey: () => 'extensions',
	getData: (_route, { tenantContext: { cloudId, isAnonymous } }) =>
		fetchModules({
			cloudId,
			isAnonymous,
			types: [CUSTOM_FIELD_TYPE_MODULE],
			context: {},
			includeHidden: false,
			source: SOURCE_NAVIGATION,
		}).then(
			// oxlint-disable-next-line typescript/consistent-type-assertions
			(modules) => modules[CUSTOM_FIELD_TYPE_MODULE] as CustomFieldType[],
		),
	maxAge: Number.MAX_SAFE_INTEGER,
}) as RouteResource<CustomFieldType[]>;

/**
 * Makes sure modules are filtered out depending on the predicate and sorted alphabetically by title.
 */
function normalizeModules<T extends FullPage | BoardViewActions>(
	modules: T[],
	filter: (modules: T[]) => T[],
) {
	return filter(modules).sort((a, b) => alphabeticalSort(a.properties.title, b.properties.title));
}
